import { useState } from 'react';
import { Tag, Timeline, Tooltip, Typography } from 'antd';
import {
  CaretDownOutlined,
  CaretRightOutlined,
  CheckCircleFilled,
  ClockCircleFilled,
  ExperimentOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import type { RuleProcessingStatus } from './types';
import type { ResolvedMethod, ResolvedTimelineEntry } from './ruleTimeline';

const { Text } = Typography;

const deltaTagStyle: React.CSSProperties = {
  fontSize: 11,
  padding: '0 6px',
  lineHeight: '18px',
  margin: 0,
};

interface StatusVisual {
  dotColor: 'green' | 'gold' | 'gray';
  dotIcon: React.ReactNode;
  tagColor?: string;
  tagStyle?: React.CSSProperties;
  tagLabel: string;
}

const STATUS_VISUALS: Record<RuleProcessingStatus, StatusVisual> = {
  applied: {
    dotColor: 'green',
    dotIcon: <CheckCircleFilled style={{ fontSize: 16 }} />,
    tagColor: 'green',
    tagLabel: 'Applied',
  },
  wouldHaveApplied: {
    dotColor: 'gold',
    dotIcon: <ClockCircleFilled style={{ fontSize: 16 }} />,
    tagColor: 'gold',
    tagLabel: 'Would Have Applied',
  },
  skipped: {
    dotColor: 'gray',
    dotIcon: <MinusCircleOutlined style={{ fontSize: 16, color: 'rgba(0,0,0,0.35)' }} />,
    tagStyle: { color: 'rgba(0,0,0,0.55)', borderColor: 'rgba(0,0,0,0.15)' },
    tagLabel: 'Does Not Apply',
  },
};

const INACTIVE_TOOLTIP = 'Normally inactive — included via the Rules to Include selector for this test run.';

const PILL_COLLAPSE_THRESHOLD = 5;

const DeltaDisclosure = ({
  isOpen,
  onToggle,
  children,
}: {
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) => (
  <div
    role="button"
    tabIndex={0}
    onClick={onToggle}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onToggle();
      }
    }}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      cursor: 'pointer',
      userSelect: 'none',
    }}
  >
    {isOpen ? (
      <CaretDownOutlined style={{ fontSize: 10, color: 'rgba(0,0,0,0.45)' }} />
    ) : (
      <CaretRightOutlined style={{ fontSize: 10, color: 'rgba(0,0,0,0.45)' }} />
    )}
    <Text type="secondary" style={{ fontSize: 11 }}>
      {children}
    </Text>
  </div>
);

export interface RuleTimelineItemProps {
  entry: ResolvedTimelineEntry;
  className?: string;
}

export const RuleTimelineItem = ({ entry, className }: RuleTimelineItemProps) => {
  const { rule, status } = entry;
  const visuals = STATUS_VISUALS[status];
  const nameStyle: React.CSSProperties = {
    ...(status === 'skipped' ? { color: 'rgba(0,0,0,0.55)' } : {}),
    ...(rule.inactive ? { fontStyle: 'italic' } : {}),
  };

  return (
    <Timeline.Item key={rule.ruleId} className={className} color={visuals.dotColor} dot={visuals.dotIcon}>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
        <Text type="secondary" style={{ marginRight: 2 }}>
          {rule.position}.
        </Text>
        {rule.inactive ? (
          <Tooltip title={INACTIVE_TOOLTIP}>
            <ExperimentOutlined style={{ color: 'rgba(0,0,0,0.45)', fontSize: 13 }} />
          </Tooltip>
        ) : null}
        <Text strong style={nameStyle}>
          {rule.name}
        </Text>
        <Tag color={visuals.tagColor} style={{ marginLeft: 4, ...visuals.tagStyle }}>
          {visuals.tagLabel}
        </Tag>
        {status === 'applied' && rule.terminating ? (
          <Tag color="red" style={{ marginLeft: 0 }}>
            Stopped Processing
          </Tag>
        ) : null}
      </div>
      <RuleFooter entry={entry} />
    </Timeline.Item>
  );
};

// Footer dispatch — only `applied` rules show anything below the header. The three branches
// are mutually exclusive, so they share one `expanded` state for disclosure toggling.
const RuleFooter = ({ entry }: { entry: ResolvedTimelineEntry }) => {
  const { rule, status, added, removed, appliedAfter } = entry;
  const [expanded, setExpanded] = useState(false);

  if (status !== 'applied') return null;
  const toggle = () => setExpanded((v) => !v);

  if (rule.listType === 'allow' && appliedAfter) {
    return <UseOnlyPoolRow appliedAfter={appliedAfter} expanded={expanded} onToggle={toggle} />;
  }
  if (added.length > 0 || removed.length > 0) {
    return <PlainDeltaRow added={added} removed={removed} expanded={expanded} onToggle={toggle} />;
  }
  return (
    <div style={{ marginTop: 4, fontSize: 12 }}>
      <Text type="secondary">No change to list</Text>
    </div>
  );
};

const UseOnlyPoolRow = ({
  appliedAfter,
  expanded,
  onToggle,
}: {
  appliedAfter: ResolvedMethod[];
  expanded: boolean;
  onToggle: () => void;
}) => {
  // USE ONLY rules replace the entire pool. Add/remove diff would bury that intent under a
  // wall of red strikethroughs (every previously-eligible method becomes a "removal"), so we
  // render the post-rule pool directly as outline pills with a small caption.
  if (appliedAfter.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
        <Text type="secondary" style={{ fontSize: 11 }}>
          List now:
        </Text>
        <Text type="secondary" italic style={{ fontSize: 11 }}>
          Empty
        </Text>
      </div>
    );
  }

  const pills = appliedAfter.map(({ id, name }) => (
    <Tag key={`useonly-${id}`} style={deltaTagStyle}>
      {name}
    </Tag>
  ));

  if (appliedAfter.length <= PILL_COLLAPSE_THRESHOLD) {
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4, marginTop: 4 }}>
        <Text type="secondary" style={{ fontSize: 11 }}>
          List now:
        </Text>
        {pills}
      </div>
    );
  }

  return (
    <div style={{ marginTop: 4 }}>
      <DeltaDisclosure isOpen={expanded} onToggle={onToggle}>
        List now: {appliedAfter.length} methods
      </DeltaDisclosure>
      {expanded ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 4 }}>{pills}</div> : null}
    </div>
  );
};

const PlainDeltaRow = ({
  added,
  removed,
  expanded,
  onToggle,
}: {
  added: ResolvedMethod[];
  removed: ResolvedMethod[];
  expanded: boolean;
  onToggle: () => void;
}) => {
  const pills = (
    <>
      {added.map(({ id, name }) => (
        <Tag key={`added-${id}`} color="green" style={deltaTagStyle}>
          + {name}
        </Tag>
      ))}
      {removed.map(({ id, name }) => (
        <Tag key={`removed-${id}`} color="red" style={{ ...deltaTagStyle, textDecoration: 'line-through' }}>
          {name}
        </Tag>
      ))}
    </>
  );

  const total = added.length + removed.length;
  if (total <= PILL_COLLAPSE_THRESHOLD) {
    return <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 4 }}>{pills}</div>;
  }

  const summaryParts: string[] = [];
  if (added.length > 0) summaryParts.push(`${added.length} service methods added`);
  if (removed.length > 0) summaryParts.push(`${removed.length} service methods removed`);

  return (
    <div style={{ marginTop: 4 }}>
      <DeltaDisclosure isOpen={expanded} onToggle={onToggle}>
        {summaryParts.join(', ')}
      </DeltaDisclosure>
      {expanded ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 4 }}>{pills}</div> : null}
    </div>
  );
};
